import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth, API } from '../context/AuthContext';
import { LogOut, Mail, Phone, Building2, Globe2, Calendar, Trash2, ChevronDown, Search, RefreshCw, FileText, Ship, Briefcase } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { useToast } from '../hooks/use-toast';

const SERVICIO_LABELS = {
  importacion: { label: 'Importación', icon: Ship },
  exportacion: { label: 'Exportación', icon: Globe2 },
  internacionalizacion: { label: 'Internacionalización', icon: Building2 },
  modelo: { label: 'Modelo de Negocio', icon: Briefcase },
};

const STATUS_OPTIONS = [
  { value: 'nuevo', label: 'Nuevo', color: 'bg-emerald-100 text-emerald-800' },
  { value: 'en_proceso', label: 'En proceso', color: 'bg-amber-100 text-amber-800' },
  { value: 'cerrado', label: 'Cerrado', color: 'bg-neutral-200 text-neutral-700' },
];

function StatCard({ label, value, accent }) {
  return (
    <div className="bg-white border border-neutral-200 rounded-xl p-5">
      <div className="text-xs text-neutral-500 tracking-wide uppercase">{label}</div>
      <div className={`text-3xl font-bold mt-2 ${accent || 'text-[#07301f]'}`}>{value}</div>
    </div>
  );
}

function statusBadge(status) {
  const opt = STATUS_OPTIONS.find((o) => o.value === status) || STATUS_OPTIONS[0];
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${opt.color}`}>
      <span className="w-1.5 h-1.5 rounded-full bg-current" />
      {opt.label}
    </span>
  );
}

function formatDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString('es-CL', { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return iso;
  }
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [contacts, setContacts] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('todos');
  const [filterServicio, setFilterServicio] = useState('todos');
  const [expanded, setExpanded] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [c, s] = await Promise.all([
        axios.get(`${API}/admin/contacts`),
        axios.get(`${API}/admin/stats`),
      ]);
      setContacts(c.data);
      setStats(s.data);
    } catch (e) {
      toast({ title: 'Error al cargar consultas', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login', { replace: true });
  };

  const updateStatus = async (id, status) => {
    try {
      const res = await axios.patch(`${API}/admin/contacts/${id}`, { status });
      setContacts((cs) => cs.map((c) => (c.id === id ? res.data : c)));
      toast({ title: 'Estado actualizado' });
      load();
    } catch (e) {
      toast({ title: 'Error al actualizar', variant: 'destructive' });
    }
  };

  const removeContact = async (id) => {
    if (!window.confirm('¿Eliminar esta consulta?')) return;
    try {
      await axios.delete(`${API}/admin/contacts/${id}`);
      setContacts((cs) => cs.filter((c) => c.id !== id));
      toast({ title: 'Consulta eliminada' });
      load();
    } catch (e) {
      toast({ title: 'Error al eliminar', variant: 'destructive' });
    }
  };

  const filtered = contacts.filter((c) => {
    if (filterStatus !== 'todos' && c.status !== filterStatus) return false;
    if (filterServicio !== 'todos' && c.servicio !== filterServicio) return false;
    if (query) {
      const q = query.toLowerCase();
      const hay = [c.empresa, c.contactoNombre, c.contactoEmail, c.pais, c.producto].some(
        (v) => (v || '').toLowerCase().includes(q),
      );
      if (!hay) return false;
    }
    return true;
  });

  return (
    <div className="min-h-screen bg-neutral-50">
      {/* Header */}
      <header className="bg-[#07301f] text-white">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 relative flex items-center justify-center">
              <div className="absolute inset-0 rotate-45 border-2 border-white/90 rounded-sm" />
              <span className="relative text-white font-serif text-sm font-semibold">PL</span>
            </div>
            <div>
              <div className="text-[10px] tracking-[0.3em] uppercase text-white/60">Panel de administración</div>
              <div className="text-white font-semibold tracking-wide">Patagonia Lobby</div>
            </div>
          </div>
          <div className="flex items-center gap-4">
            {user && (
              <div className="hidden md:flex items-center gap-3">
                {user.picture && <img src={user.picture} alt="" className="w-9 h-9 rounded-full border border-white/20" />}
                <div className="text-right">
                  <div className="text-white text-sm font-medium">{user.name}</div>
                  <div className="text-white/60 text-xs">{user.email}</div>
                </div>
              </div>
            )}
            <Button onClick={handleLogout} variant="outline" className="border-white/60 bg-transparent text-white hover:bg-white hover:text-[#07301f] rounded-md h-10">
              <LogOut className="w-4 h-4 mr-2" /> Salir
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Consultas recibidas</h1>
            <p className="text-neutral-600 mt-1 text-sm">Todas las solicitudes enviadas desde el formulario del sitio web.</p>
          </div>
          <Button onClick={load} variant="outline" className="h-10">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Actualizar
          </Button>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <StatCard label="Total" value={stats.total} />
            <StatCard label="Nuevos" value={stats.nuevos} accent="text-emerald-600" />
            <StatCard label="En proceso" value={stats.en_proceso} accent="text-amber-600" />
            <StatCard label="Cerrados" value={stats.cerrado} accent="text-neutral-600" />
          </div>
        )}

        {/* Filters */}
        <div className="bg-white border border-neutral-200 rounded-xl p-4 mb-6 flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Buscar por empresa, contacto, email, país…"
              className="pl-9"
            />
          </div>
          <Select value={filterServicio} onValueChange={setFilterServicio}>
            <SelectTrigger className="w-[200px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">Todos los servicios</SelectItem>
              {Object.entries(SERVICIO_LABELS).map(([k, v]) => (
                <SelectItem key={k} value={k}>{v.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-[180px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">Todos los estados</SelectItem>
              {STATUS_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* List */}
        <div className="space-y-3">
          {loading && (
            <div className="text-center py-16 text-neutral-500">Cargando…</div>
          )}
          {!loading && filtered.length === 0 && (
            <div className="text-center py-20 bg-white border border-dashed border-neutral-300 rounded-xl">
              <Mail className="w-10 h-10 text-neutral-300 mx-auto mb-3" />
              <p className="text-neutral-500">No hay consultas para los filtros seleccionados.</p>
            </div>
          )}
          {filtered.map((c) => {
            const servicio = SERVICIO_LABELS[c.servicio] || { label: c.servicio, icon: FileText };
            const Icon = servicio.icon;
            const isOpen = expanded === c.id;
            return (
              <div key={c.id} className="bg-white border border-neutral-200 rounded-xl overflow-hidden">
                <div className="p-5 flex flex-col md:flex-row md:items-center gap-4 cursor-pointer hover:bg-neutral-50" onClick={() => setExpanded(isOpen ? null : c.id)}>
                  <div className="w-11 h-11 rounded-full bg-[#07301f]/10 text-[#07301f] flex items-center justify-center shrink-0">
                    <Icon className="w-5 h-5" strokeWidth={1.75} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <h3 className="text-base font-semibold text-[#0a0a0a] truncate">{c.empresa}</h3>
                      <Badge variant="outline" className="text-xs border-neutral-300">{servicio.label}</Badge>
                      {statusBadge(c.status)}
                    </div>
                    <div className="text-sm text-neutral-600 flex flex-wrap gap-x-4 gap-y-1">
                      <span className="inline-flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" />{c.contactoEmail}</span>
                      {c.contactoTelefono && <span className="inline-flex items-center gap-1.5"><Phone className="w-3.5 h-3.5" />{c.contactoTelefono}</span>}
                      {c.pais && <span className="inline-flex items-center gap-1.5"><Globe2 className="w-3.5 h-3.5" />{c.pais}</span>}
                      <span className="inline-flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" />{formatDate(c.created_at)}</span>
                    </div>
                  </div>
                  <ChevronDown className={`w-5 h-5 text-neutral-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                </div>

                {isOpen && (
                  <div className="border-t border-neutral-200 p-6 bg-neutral-50/50 grid md:grid-cols-2 gap-5 text-sm">
                    <Field label="Contacto">{c.contactoNombre}</Field>
                    <Field label="Email">{c.contactoEmail}</Field>
                    {c.contactoTelefono && <Field label="Teléfono">{c.contactoTelefono}</Field>}
                    <Field label="País">{c.pais}</Field>
                    {c.producto && <Field label="Producto">{c.producto}</Field>}
                    {c.posicionArancelaria && <Field label="Posición arancelaria">{c.posicionArancelaria}</Field>}
                    {c.paisOrigen && <Field label="País de origen">{c.paisOrigen}</Field>}
                    {c.cantidad && <Field label="Cantidad">{c.cantidad}</Field>}
                    {c.incoterm && <Field label="Incoterm">{c.incoterm}</Field>}
                    {c.puertoIngreso && <Field label="Puerto">{c.puertoIngreso}</Field>}
                    {c.mercadoObjetivo && <Field label="Mercado objetivo">{c.mercadoObjetivo}</Field>}
                    {c.volumen && <Field label="Volumen">{c.volumen}</Field>}
                    {c.documentacion && <Field label="Documentación">{c.documentacion}</Field>}
                    {c.observaciones && (
                      <div className="md:col-span-2">
                        <div className="text-xs text-neutral-500 uppercase tracking-wide mb-1">Observaciones</div>
                        <p className="text-neutral-800 whitespace-pre-wrap">{c.observaciones}</p>
                      </div>
                    )}

                    <div className="md:col-span-2 flex flex-wrap items-center gap-3 pt-4 border-t border-neutral-200">
                      <Select value={c.status} onValueChange={(v) => updateStatus(c.id, v)}>
                        <SelectTrigger className="w-[180px]"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {STATUS_OPTIONS.map((o) => (
                            <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <a
                        href={`mailto:${c.contactoEmail}?subject=Su solicitud - Patagonia Lobby`}
                        className="inline-flex items-center gap-2 h-10 px-4 rounded-md bg-[#07301f] text-white text-sm font-medium hover:bg-[#0a4a31]"
                      >
                        <Mail className="w-4 h-4" /> Responder
                      </a>
                      <Button variant="outline" onClick={() => removeContact(c.id)} className="ml-auto text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700">
                        <Trash2 className="w-4 h-4 mr-2" /> Eliminar
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </main>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="text-xs text-neutral-500 uppercase tracking-wide mb-1">{label}</div>
      <div className="text-neutral-800">{children}</div>
    </div>
  );
}
